import Drawer from '@/components/Drawer';
import { useSubmitOnEnter } from '@/components/Form/useSubmitOnEnter';
import { EMAIL_CENTER_ACTIONS } from '@/constants/actions';
import { readPaginator } from '@/models/emailCenter';
import * as EmailCenterSelectors from '@/selectors/emailCenterSelectors';
import {
  createMarketingAutomationRequest,
  fetchEmailTemplatesRequest,
  updateMarketingAutomationRequest,
} from '@/services/api/emailCenter';
import {
  EmailTemplateModel,
  LaravelPaginator,
  MarketingAutomationModel,
} from '@/typings/api/emailCenter';
import { applyServerErrorsToForm, normalizeApiError } from '@/utils/apiErrors';
import { F, getIntlTranslation, INTL_ACTION, INTL_ENTITY } from '@/utils/intl';
import { processAPIResponseNow } from '@/utils/request';
import { Alert, Button, Form, Input, Space, Typography } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useIntl, useSelector } from 'umi';
import { TEMPLATE_POOL_PAGE_SIZE } from '../../constants';
import SectionsBuilder from './SectionsBuilder';
import {
  areSectionsValid,
  createEmptySection,
  FormSection,
  resourceToFormSections,
  sectionsToPayload,
} from './sections';

import { useEmailCenterValidateMessages } from '../../useValidateMessages';
import styles from './AutomationsTab.less';

const AutomationDrawer: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const validateMessages = useEmailCenterValidateMessages();
  const [form] = Form.useForm();

  const drawer = useSelector(EmailCenterSelectors.selectDrawer('automation'));
  const journeyHashId = useSelector(
    EmailCenterSelectors.selectSelectedJourneyHashId,
  );

  const [saving, setSaving] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [pickerTemplates, setPickerTemplates] = useState<EmailTemplateModel[]>(
    [],
  );

  const record = drawer.record as MarketingAutomationModel | null;
  const isEdit = drawer.mode === 'edit' && record != null;

  useEffect(() => {
    if (!drawer.visible) return;
    setGeneralError(null);

    // Marketing templates for the section pickers (single max-size page).
    fetchEmailTemplatesRequest({
      query: { type: 'marketing', page_size: TEMPLATE_POOL_PAGE_SIZE },
    }).then((result) =>
      processAPIResponseNow(
        result,
        {
          onSuccess: (paginator: LaravelPaginator<EmailTemplateModel>) => {
            const { list } = readPaginator(paginator);
            setPickerTemplates(list);
            // Template resolution needs the picker list (hash_id → uuid gap),
            // so the form seeds after the fetch when editing.
            if (isEdit && record) {
              form.setFieldsValue({
                name: record.name,
                sections: resourceToFormSections(record, list),
              });
            }
          },
        },
        'data',
      ),
    );

    if (!isEdit) {
      form.resetFields();
      form.setFieldsValue({ sections: [createEmptySection()] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawer.visible, record?.id]);

  const close = useCallback(() => {
    dispatch({
      type: EMAIL_CENTER_ACTIONS.CLOSE_DRAWER,
      payload: { drawer: 'automation' },
    });
  }, [dispatch]);

  const handleSave = useCallback(async () => {
    if (!journeyHashId) return;
    const values = await form.validateFields();
    const payload = {
      name: values.name,
      sections: sectionsToPayload(values.sections as FormSection[]),
    };

    setSaving(true);
    setGeneralError(null);
    const result = isEdit
      ? await updateMarketingAutomationRequest({
          journeyHashId,
          automationId: record!.id,
          payload,
        })
      : await createMarketingAutomationRequest({ journeyHashId, payload });
    setSaving(false);

    processAPIResponseNow(result, {
      showSuccessMessage: true,
      successEntity: INTL_ENTITY.MARKETING_AUTOMATION,
      onSuccess: () => {
        close();
        dispatch({
          type: EMAIL_CENTER_ACTIONS.FETCH_AUTOMATIONS,
          payload: { journeyHashId },
        });
      },
      onError: (data) => {
        // Includes the DOI-inactive 422 on create — surfaced as the alert.
        const normalized = normalizeApiError(data);
        applyServerErrorsToForm(form, normalized.fieldErrors, {
          onUnmapped: (key, messages) =>
            setGeneralError((prev) => prev ?? `${key}: ${messages.join(' ')}`),
        });
        if (Object.keys(normalized.fieldErrors).length === 0) {
          setGeneralError(normalized.message);
        }
      },
    });
  }, [journeyHashId, form, isEdit, record, close, dispatch]);

  const submitOnEnter = useSubmitOnEnter(handleSave, { enabled: !saving });

  return (
    <Drawer
      width={720}
      open={drawer.visible}
      onOpenChange={close}
      title={
        <F
          entity={INTL_ENTITY.MARKETING_AUTOMATION}
          action={isEdit ? INTL_ACTION.EDIT : INTL_ACTION.CREATE}
        />
      }
      footer={
        <Space className={styles.drawerFooter}>
          <Button onClick={close}>
            <F id="common.actions.cancel" defaultMessage="Cancel" />
          </Button>
          <Button type="primary" loading={saving} onClick={handleSave}>
            <F id="common.actions.save" defaultMessage="Save" />
          </Button>
        </Space>
      }
    >
      {generalError && (
        <Alert
          className={styles.generalError}
          type="error"
          showIcon
          message={generalError}
        />
      )}

      <Typography.Paragraph type="secondary">
        <F
          id="pages.emailCenter.automations.doiHint"
          defaultMessage="Automations only run for journeys with double opt-in (DOI) active."
        />
      </Typography.Paragraph>

      <Form
        validateMessages={validateMessages}
        form={form}
        layout="vertical"
        {...submitOnEnter}
      >
        <Form.Item
          name="name"
          label={<F id="common.attribute.name" defaultMessage="Name" />}
          rules={[{ required: true }, { max: 255 }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="sections"
          label={
            <F
              id="pages.emailCenter.automations.sections"
              defaultMessage="Steps"
            />
          }
          rules={[
            {
              validator: (_, sections: FormSection[] = []) =>
                areSectionsValid(sections)
                  ? Promise.resolve()
                  : Promise.reject(
                      getIntlTranslation(intl, {
                        id: 'pages.emailCenter.automations.sectionsInvalid',
                        defaultMessage:
                          'Every step needs a template and a non-negative wait.',
                      }),
                    ),
            },
          ]}
        >
          <SectionsBuilder templates={pickerTemplates} />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default AutomationDrawer;
